import { useMemo, useState } from "react";
import { Building2, Check, ChevronDown, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import type { Property } from "@shared/schema";
import { cn } from "@/lib/utils";
import { sortProperties } from "@/lib/property-utils";

/** Sentinel id used to represent "no property / null propertyId" in the filter. */
export const NO_PROPERTY_SENTINEL = 0;

interface PropertyMultiSelectFilterProps {
  properties: Property[];
  value: number[];
  onChange: (value: number[]) => void;
  className?: string;
  /** When true, renders a "— No property —" entry at the top of the list.
   *  Selecting it adds `NO_PROPERTY_SENTINEL` (0) to the value array. */
  noPropertyOption?: boolean;
}

export function PropertyMultiSelectFilter({
  properties,
  value,
  onChange,
  className,
  noPropertyOption = false,
}: PropertyMultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = useMemo(() => new Set(value), [value]);

  const sorted = useMemo(() => sortProperties(properties), [properties]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => String(p.name ?? "").toLowerCase().includes(q));
  }, [sorted, search]);

  const triggerLabel = useMemo(() => {
    if (value.length === 0) return "All Properties";
    const realCount = value.filter((id) => id !== NO_PROPERTY_SENTINEL).length;
    const hasNone = value.includes(NO_PROPERTY_SENTINEL);
    if (value.length === 1) {
      if (hasNone) return "No property";
      const match = properties.find((p) => p.id === value[0]);
      return match?.name ?? "1 property";
    }
    if (hasNone && realCount === 0) return "No property";
    return `${value.length} selected`;
  }, [value, properties]);

  const toggle = (id: number) => {
    if (selected.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const selectAll = () => {
    const ids = filtered.map((p) => p.id);
    if (noPropertyOption && !search.trim()) ids.unshift(NO_PROPERTY_SENTINEL);
    onChange(ids);
  };

  const clear = () => {
    onChange([]);
  };

  if (properties.length === 0 && !noPropertyOption) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-9 justify-between gap-2 text-sm font-normal", className)}
          data-testid="button-property-filter"
        >
          <span className="flex items-center gap-1 truncate">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate" title={triggerLabel}>{triggerLabel}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search properties..."
              className="h-8 pl-8 text-sm"
              data-testid="input-property-filter-search"
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="text-xs font-medium text-blue-600 hover:underline"
              onClick={selectAll}
              data-testid="button-property-filter-select-all"
            >
              Select all
            </button>
            <button
              type="button"
              className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900"
              onClick={clear}
              data-testid="button-property-filter-clear"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {noPropertyOption && !search.trim() && (
            <label
              key="__none__"
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-100"
              data-testid="row-property-filter-none"
            >
              <Checkbox
                checked={selected.has(NO_PROPERTY_SENTINEL)}
                onCheckedChange={() => toggle(NO_PROPERTY_SENTINEL)}
                data-testid="checkbox-property-filter-none"
              />
              <span className="truncate text-muted-foreground italic">— No property —</span>
              {selected.has(NO_PROPERTY_SENTINEL) && <Check className="ml-auto h-3 w-3 text-blue-600" />}
            </label>
          )}
          {filtered.length === 0 ? (
            <div className="px-2 py-4 text-center text-xs text-muted-foreground">
              No properties found
            </div>
          ) : (
            filtered.map((p) => {
              const isChecked = selected.has(p.id);
              return (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-100"
                  data-testid={`row-property-filter-${p.id}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggle(p.id)}
                    data-testid={`checkbox-property-filter-${p.id}`}
                  />
                  <span className="truncate" title={p.name || undefined}>{p.name}</span>
                  {isChecked && <Check className="ml-auto h-3 w-3 text-blue-600" />}
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
